/** Deal drawer — per-deal Expenses, v1 txn-expenses.js txnExpensesPanel parity
 * (rebuilt 2026-07-19: "the expense tab is completely different than v1").
 * Always-open panel: header (N entries · Total, Export CSV, + Add Expense),
 * search box, the 7-column table (Date / Description / Company / Category with
 * its colored dot / Amount / Total w/Tax / Status + one-click delete — v1's
 * delTxnExp has no confirm), and a footer totals row. The Add form picks
 * vendors from Contacts and auto-fills company + category from the vendor's
 * contractor type (v1 vendorContacts/exPickVendor/CTYPE_EXPCAT). Reads the
 * TXNEXP org-settings array filtered to this deal; add / delete write the
 * WHOLE array through the settings door (PHASE19), preserving every other
 * deal's rows. Gated by liveFire `expensesEdit`. */
import { useMemo, useRef, useState } from "react";
import { liveOn } from "../../lib/liveFire";
import { diag } from "../../lib/diag";
import {
  displayToIso, fmtMoneyExact,
  EXP_STATUS, EXPENSE_MSG, buildExpenseCsv,
  allTxnExpensesRaw, narrowTxnExpense, txnExpenseToRaw, txnTotals,
  isoToDisplay, newExpenseCid, expenseBlocker,
  type TxnExpense,
} from "./api";
import type { Contact } from "../contacts/api";

const money = fmtMoneyExact; // exact cents — 10,026.73 stays 10,026.73 (Tom 2026-07-24)
const normKey = (s: string): string => String(s ?? "").trim().toLowerCase();
const streetSeg = (k: string): string => (k.split(",")[0] ?? "").trim();

/** v1 EXP_CATC — category → dot color (Tailwind *-400 hues as hex). */
const CAT_DOT: Record<string, string> = {
  Roof: "#fb923c", Rehab: "#60a5fa", Closing: "#a78bfa", Selling: "#34d399",
  Marketing: "#fbbf24", Plumbing: "#38bdf8", Electrical: "#facc15",
  Flooring: "#fb7185", HVAC: "#22d3ee",
};
const catDot = (c: string): string => CAT_DOT[c] ?? "#94a3b8";
/** v1's Add-Expense category list = Object.keys(EXP_CATC), in order. */
const DEAL_EXP_CATS = ["Roof", "Rehab", "Closing", "Selling", "Marketing", "Plumbing", "Electrical", "Flooring", "HVAC", "Other"];

/** v1 CTYPE_EXPCAT — vendor contractorType → default expense category. */
const CTYPE_EXPCAT: Record<string, string> = {
  gc: "Rehab", like_gc: "Rehab", electrician: "Electrical", plumbing: "Plumbing",
  roof: "Roof", ac: "HVAC", yard: "Other", inspector: "Other",
  refinisher: "Rehab", floors: "Flooring", staging: "Selling", photographer: "Selling",
};

interface VendorOpt { name: string; company: string; type: string; cat: string }

/** v1 vendorContacts(): contacts that are contractors/vendors, with the
 * company to fill in and the category their trade maps to. */
function vendorOptions(contacts: Contact[]): VendorOpt[] {
  const out: VendorOpt[] = [];
  for (const c of contacts) {
    const ct = typeof c.raw["contractorType"] === "string" ? (c.raw["contractorType"] as string) : "";
    const isVendor = !!ct || c.raw["r"] === "Contractor" || c.tags.some((t) => t === "Vendor" || t === "Contractor");
    if (!isVendor) continue;
    out.push({ name: c.name, company: c.company ?? "", type: c.type || "Vendor", cat: CTYPE_EXPCAT[ct] ?? "Rehab" });
  }
  return out;
}

function downloadCsv(name: string, csv: string): void {
  try {
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch { /* non-DOM env */ }
}

export function TxnExpensesSection({
  address,
  orgSettings,
  contacts = [],
  onSave,
  canWrite = true,
  probing = false,
}: {
  address: string;
  orgSettings: Record<string, unknown>;
  /** all contacts — the Add form's "Pick a vendor from Contacts" dropdown. */
  contacts?: Contact[];
  /** returns whether the write was ACCEPTED (queued). `false` = refused
   * (settings not loaded yet) — the form stays open and says so. `void`
   * from older callers counts as accepted. */
  onSave: (all: Record<string, unknown>[]) => boolean | void;
  /** the DEAL write gate — the one that decides whether onSave does anything */
  canWrite?: boolean;
  /** that gate is still being probed; not yet known to be closed */
  probing?: boolean;
}) {
  // BOTH gates, deliberately. This section used to consult only liveOn(),
  // while the onSave it calls was gated on the deal write-probe up in
  // DealsPage. When the two disagreed the Add form rendered fully enabled and
  // threw the expense away on submit — no error, no queued op, no trace.
  // 2026-07-27, Tom: "i tried adding twice and it didnt do anything."
  const canEdit = canWrite && liveOn("expensesEdit");
  const [adding, setAdding] = useState(false);
  const [editIdx, setEditIdx] = useState<number | null>(null); // global TXNEXP index being edited
  const [q, setQ] = useState("");

  // deal's rows WITH their index in the full array (delete without clobbering).
  // allCats: every category used on ANY deal (2026-08-11, Tom: "adding a new
  // category doesnt persist and unable to choose from again") — a custom
  // category typed once now shows up org-wide, because TXNEXP is one global
  // array and the pick list feeds from ALL of it, not just this deal's rows.
  const { entries, totals, allCats } = useMemo(() => {
    const all = allTxnExpensesRaw(orgSettings);
    const want = normKey(address);
    const wantStreet = streetSeg(want);
    const es: { e: TxnExpense; idx: number }[] = [];
    const cats: string[] = [];
    all.forEach((r, idx) => {
      const e = narrowTxnExpense(r);
      if (e.cat) cats.push(e.cat);
      const dk = normKey(e.deal);
      if (dk === want || (wantStreet && streetSeg(dk) === wantStreet)) es.push({ e, idx });
    });
    return { entries: es, totals: txnTotals(es.map((x) => x.e)), allCats: [...new Set(cats)] };
  }, [orgSettings, address]);

  // v1 domFilter: live text filter across the row's visible fields.
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return entries;
    return entries.filter(({ e }) =>
      (e.date + " " + e.desc + " " + e.company + " " + e.cat + " " + e.status).toLowerCase().includes(needle));
  }, [entries, q]);

  // Why the last save didn't take — shown INSIDE the section, next to the
  // form. 2026-09-02, Tom: "i clicked add and nothing happened": a refused
  // save used to close the form and whisper the reason in the sync pill.
  const [saveErr, setSaveErr] = useState<string | null>(null);
  /** the open Add/Edit form's submit — the header button drives it */
  const submitRef = useRef<(() => void) | null>(null);
  const save = (mutate: (all: Record<string, unknown>[]) => void): boolean => {
    const all = allTxnExpensesRaw(orgSettings).map((r) => ({ ...r }));
    mutate(all);
    const r = onSave(all);
    const ok = r !== false;
    diag("expense.save", { accepted: ok, ret: r === undefined ? "void" : String(r), n: all.length, canEdit });
    setSaveErr(ok ? null : EXPENSE_MSG.notLoaded);
    return ok;
  };
  const add = (e: TxnExpense) => {
    if (save((all) => { all.unshift(txnExpenseToRaw({ ...e, deal: address, cid: e.cid || newExpenseCid() })); })) setAdding(false);
  };
  // edit-in-place: replace the row at its GLOBAL index, keeping its deal key
  const editRow = (idx: number, e: TxnExpense) => {
    const ok = save((all) => {
      const prev = all[idx];
      if (prev) all[idx] = txnExpenseToRaw({ ...e, deal: narrowTxnExpense(prev).deal || address, cid: e.cid || narrowTxnExpense(prev).cid || newExpenseCid() });
    });
    if (ok) setEditIdx(null);
  };
  const remove = (idx: number) => save((all) => { if (idx >= 0 && idx < all.length) all.splice(idx, 1); });

  // v1 txnExpExport — same columns, same order.
  const exportCsv = () =>
    downloadCsv("expenses.csv", buildExpenseCsv(
      ["Date", "Description", "Company", "Category", "Amount", "Total w/Tax", "Status"],
      entries.map(({ e }) => [e.date, e.desc, e.company, e.cat, e.amount, e.tax, e.status]),
    ));

  return (
    <div className="txnexp">
      {!canEdit ? (
        <div className="expqbo">
          {probing
            ? "Checking write access… saves are not live yet — give it a moment."
            : EXPENSE_MSG.editPhaseB}
          {" — this shows this deal's expense ledger."}
        </div>
      ) : null}

      {/* v1 header row: title + "N entries · Total: $X" left, Export + Add right */}
      <div className="expheadrow">
        <div>
          <div className="expheadtitle">Expenses</div>
          <div className="sub">{entries.length} entr{entries.length === 1 ? "y" : "ies"} · Total: {money(totals.tax)}</div>
        </div>
        <div className="expheadacts">
          <button className="btn slim" onClick={exportCsv}>⬇ Export</button>
          {canEdit ? (
            <button
              className="btn slim primary"
              title={adding || editIdx !== null ? "Save the expense you're filling in" : "Add an expense to this deal"}
              onClick={() => {
                // 2026-09-14: the header button and the form's own button both
                // said "Add Expense", and this one TOGGLED the form — so Tom's
                // click here closed his filled-in form with nothing saved, every
                // time since 2026-09-02 (his diag trail: no submit ever ran).
                // Now, with a form open, this button saves it.
                diag("expense.headerclick", { adding, editing: editIdx !== null });
                if (adding || editIdx !== null) { submitRef.current?.(); return; }
                setAdding(true);
              }}
            >{adding || editIdx !== null ? "✓ Save Expense" : "+ Add Expense"}</button>
          ) : null}
        </div>
      </div>

      {saveErr ? <div className="expsaveerr" role="alert">⚠ {saveErr}</div> : null}
      {adding && canEdit ? (
        <AddTxnExpenseForm vendors={vendorOptions(contacts)} extraCats={allCats} onCancel={() => { setAdding(false); setSaveErr(null); submitRef.current = null; }} onSubmit={add} registerSubmit={(fn) => { submitRef.current = fn; }} />
      ) : null}
      {editIdx !== null && canEdit ? (() => {
        const cur = entries.find((x) => x.idx === editIdx);
        return cur ? (
          <AddTxnExpenseForm
            vendors={vendorOptions(contacts)}
            extraCats={allCats}
            initial={cur.e}
            onCancel={() => { setEditIdx(null); submitRef.current = null; }}
            onSubmit={(e) => editRow(editIdx, e)}
            registerSubmit={(fn) => { submitRef.current = fn; }}
          />
        ) : null;
      })() : null}

      <input className="expsearch" placeholder="🔍 Search expenses…" value={q} onChange={(e) => setQ(e.target.value)} />

      <div className="list explist">
        <table className="exptable8">
          <thead>
            <tr>
              <th>#</th><th>Date</th><th>Description</th><th>Company</th><th>Category</th>
              <th className="r">Amount</th><th className="r">Total w/Tax</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {shown.length ? shown.map(({ e, idx }, i) => (
              <tr key={idx}>
                <td className="xnum dim">{i + 1}</td>
                <td className="xdate dim">{e.date || "—"}</td>
                <td className="xdesc">{e.desc || "—"}</td>
                <td className="xco dim">{e.company || "—"}</td>
                <td className="xcat"><span className="expdot" style={{ background: catDot(e.cat) }} />{e.cat}</td>
                <td className="r xamt">{money(e.amount)}</td>
                <td className="r xtax"><strong>{money(e.tax)}</strong></td>
                <td className="xst">
                  <span className={"statuschip s-" + e.status.toLowerCase()}>{e.status}</span>
                  {canEdit ? <button className="rowbtn" title="Edit this expense" onClick={() => { setEditIdx(idx); setAdding(false); }}>✎</button> : null}
                  {canEdit ? <button className="rowbtn del" title="Delete" onClick={() => remove(idx)}>✕</button> : null}
                </td>
              </tr>
            )) : (
              <tr><td className="expempty dim" colSpan={8}>{q.trim() ? "No expenses match your search." : EXPENSE_MSG.none}</td></tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={5} className="dim">{entries.length} expense{entries.length === 1 ? "" : "s"}</td>
              <td className="r"><strong>{money(totals.amount)}</strong></td>
              <td className="r"><strong>{money(totals.tax)}</strong></td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/** v1 openTxnExpense modal, as an inline card: Date + Status / Description /
 * Vendor-from-Contacts + Company + Category / Amount + Total w/ Tax. Picking a
 * vendor fills the company and auto-selects the category (v1 exPickVendor). */
const NEW_CAT = "__new__"; // sentinel for "＋ Add new category…"

function AddTxnExpenseForm({ vendors, extraCats = [], initial = null, onCancel, onSubmit, registerSubmit }: {
  vendors: VendorOpt[];
  /** categories already used on ANY deal's expenses (org-wide, 2026-08-11) —
   * a custom category typed once stays pickable everywhere, self-persisting
   * via the global TXNEXP rows */
  extraCats?: string[];
  /** when set, the form EDITS this expense instead of adding a new one */
  initial?: TxnExpense | null;
  onCancel: () => void;
  onSubmit: (e: TxnExpense) => void;
  /** hands the parent this form's submit so the header button can save too */
  registerSubmit?: (fn: () => void) => void;
}) {
  const [dateIso, setDateIso] = useState(initial ? displayToIso(initial.date) : "");
  const [desc, setDesc] = useState(initial?.desc ?? "");
  const [vendorIdx, setVendorIdx] = useState("");
  const [company, setCompany] = useState(initial?.company ?? "");
  const [cat, setCat] = useState(initial?.cat ?? (DEAL_EXP_CATS[0] ?? "Roof"));
  const [newCat, setNewCat] = useState("");
  const [amount, setAmount] = useState(initial ? String(initial.amount) : "");
  const [tax, setTax] = useState(initial ? String(initial.tax) : "");
  const [status, setStatus] = useState(initial?.status ?? "PAID");
  // built-ins ∪ this deal's already-used categories ∪ whatever a vendor pick set
  const catOpts = [...new Set([...DEAL_EXP_CATS, ...extraCats.filter(Boolean), ...(cat && cat !== NEW_CAT ? [cat] : [])])];
  const effCat = cat === NEW_CAT ? newCat.trim() : cat;
  const a = Number(amount.replace(/[^0-9.]/g, ""));
  // What still blocks the add. The button is ALWAYS clickable: pressing it
  // with something missing SAYS what — a greyed-out button that swallows the
  // click is exactly "i clicked add and nothing happened" (Tom 2026-09-02).
  const blocker = expenseBlocker({ desc, amount, cat: effCat });
  const ok = blocker === null;
  const [why, setWhy] = useState<string | null>(null);
  const pickVendor = (v: string) => {
    setVendorIdx(v);
    if (v === "") return;
    const opt = vendors[Number(v)];
    if (!opt) return;
    setCompany(opt.company || opt.name); // v1: data-name = company || name
    setCat(opt.cat); // v1 exPickVendor auto-selects the trade's category
  };
  const submit = () => {
    diag("expense.submit", { ok, why: blocker, edit: !!initial });
    if (!ok) { setWhy(blocker); return; }
    setWhy(null);
    const tRaw = tax.trim() === "" ? NaN : Number(tax.replace(/[^0-9.]/g, ""));
    // mirror defaults BOTH ways: tax (total w/ tax) defaults to amount (v1), and
    // an amount left blank takes the total — never store a $0 row with a total
    const amt = a || (Number.isFinite(tRaw) ? tRaw : 0);
    const t = Number.isFinite(tRaw) ? tRaw : amt;
    // v1 submitTxnExpense: empty date → today, display format "Jul 19, 26"
    const date = dateIso
      ? isoToDisplay(dateIso)
      : initial?.date || new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" });
    onSubmit({ deal: "", date, desc: desc.trim(), company: company.trim() || "—", cat: effCat, amount: amt, tax: t, status, ...(initial?.cid ? { cid: initial.cid } : {}) });
  };
  registerSubmit?.(submit);
  return (
    <div className="expform">
      <div className="expformtitle">{initial ? "Edit Expense" : "Add Expense"}</div>
      <div className="expformgrid">
        <label>Date<input type="date" value={dateIso} onChange={(e) => setDateIso(e.target.value)} /></label>
        <label>Status<select value={status} onChange={(e) => setStatus(e.target.value)}>{EXP_STATUS.map((s) => <option key={s}>{s}</option>)}</select></label>
        <label className="wide">Description<input autoFocus value={desc} placeholder="e.g. Roof replacement" onChange={(e) => setDesc(e.target.value)} /></label>
        {vendors.length ? (
          <label className="wide">Company / Vendor
            <select value={vendorIdx} onChange={(e) => pickVendor(e.target.value)}>
              <option value="">— Pick a vendor from Contacts —</option>
              {vendors.map((v, i) => <option key={i} value={i}>{v.name}{v.company ? " — " + v.company : ""} ({v.type})</option>)}
            </select>
          </label>
        ) : null}
        <label>Company<input value={company} placeholder="or type a company" onChange={(e) => setCompany(e.target.value)} /></label>
        <label>Category
          <select value={cat} onChange={(e) => setCat(e.target.value)}>
            {catOpts.map((c) => <option key={c}>{c}</option>)}
            <option value={NEW_CAT}>＋ Add new category…</option>
          </select>
        </label>
        {cat === NEW_CAT ? (
          <label>New category<input autoFocus value={newCat} placeholder="e.g. Landscaping" onChange={(e) => setNewCat(e.target.value)} /></label>
        ) : null}
        <label>Amount ($)<input inputMode="decimal" value={amount} placeholder="0" onChange={(e) => setAmount(e.target.value)} /></label>
        <label>Total w/ Tax ($)<input inputMode="decimal" value={tax} placeholder={amount.trim() ? amount : "(defaults to amount)"} onChange={(e) => setTax(e.target.value)} /></label>
      </div>
      <div className="expformacts">
        <button className={"btn slim primary" + (ok ? "" : " soft")} aria-disabled={!ok} onClick={submit}>{initial ? "Save changes" : "Add Expense"}</button>
        <button className="btn slim" onClick={onCancel}>Cancel</button>
        {why && !ok ? <span className="expwhy" role="alert">{why}</span> : null}
      </div>
    </div>
  );
}
